import { type ReactNode, useEffect } from 'react';
import { useLingui } from '@lingui/react/macro';

// The type only: this component renders on native, and the menu is web-only.
import type { ActionMenuItem } from '@rover/components/src/callouts/ActionMenu/ActionMenu.common';
import { Flex, Text } from '@rover/kibble/core';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import useMobileAnnounceForAccessibility from '@rover/shared/js/hooks/useMobileAnnounceForAccessibility';

import type { BulkPrivacyChange, BulkPrivacyFailure } from './hooks/useGalleryManageMode';
import useGalleryManageMode from './hooks/useGalleryManageMode';
import { GALLERY_CONTAINER_WIDTH } from './constants';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { isEditableGalleryImage, isLockedImage } from './ImageGallery.common';
import ImageGridContainer from './ImageGridContainer';
import ImageGridManageControls from './ImageGridManageControls';

type Props = {
  images: GalleryItem[] | undefined;
  onImageClick: (index: number) => void;
  onClose?: () => void;
  pagination?: PaginationConfig;
  renderImageOverlay?: (index: number) => ReactNode;
  onImagesAdded?: (files: File[]) => void;
  isUploading?: boolean;
  renderImageActions?: (index: number) => ActionMenuItem[];
  columns?: number;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isReorderable?: (index: number) => boolean;
  /** Native only. See `ImageGridContainer.native`. */
  virtualizedGrid?: boolean;
  dimmedImageIds?: Set<number>;
  lockedImageIds?: Set<number>;
  /** Whether these photos are the viewer's to curate. The host decides. */
  isViewingOwnGallery?: boolean;
  onBulkPrivacyChange?: BulkPrivacyChange;
  /** Drawn at the end of the header row, beside the manage controls. */
  headerEnd?: ReactNode;
  /** Drawn at the start of the header row, opposite the manage controls. */
  gridHeader?: ReactNode;
  /** Set when the grid sits on a dark background, as it does in the lightbox. */
  contrast?: boolean;
};

const BulkPrivacyFailureMessage = ({
  failure,
  contrast,
}: {
  failure: BulkPrivacyFailure | undefined;
  contrast: boolean;
}): JSX.Element => {
  const { t } = useLingui();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const announceOnNative = useMobileAnnounceForAccessibility();

  const failureMessage = (): string => {
    if (!failure) return '';

    const { failedCount, totalCount } = failure;
    return failedCount === totalCount
      ? t`We couldn't update these photos. Please try again.`
      : t`We couldn't update ${failedCount} of ${totalCount} photos. Please try again.`;
  };
  const message = failureMessage();

  // iOS VoiceOver ignores live regions, so native is told directly.
  useEffect(() => {
    if (!message) return;

    setScreenReaderAnnouncement(message);
    announceOnNative(message);
  }, [message, setScreenReaderAnnouncement, announceOnNative]);

  return (
    <Text size="100" textColor={contrast ? 'primary_inverse' : 'primary'}>
      {message}
    </Text>
  );
};

// The controls live here so the grid behaves the same in either host.
const ManagedGalleryGrid = ({
  images,
  isViewingOwnGallery = false,
  onBulkPrivacyChange,
  headerEnd,
  gridHeader,
  contrast = false,
  dimmedImageIds,
  lockedImageIds,
  ...gridProps
}: Props): JSX.Element => {
  const {
    isManaging,
    selectedIds,
    isSaving,
    failure,
    enterManageMode,
    cancel,
    toggleSelected,
    setSelectedPrivacy,
  } = useGalleryManageMode(onBulkPrivacyChange);

  const canManage =
    isViewingOwnGallery &&
    !!onBulkPrivacyChange &&
    !!images?.some(
      (image) => isEditableGalleryImage(image) && !isLockedImage(image, lockedImageIds)
    );

  return (
    <Flex flexDirection="column" flex="1" width="100%" justifyContent="center" gap="6x">
      {(canManage || headerEnd || gridHeader) && (
        <Flex
          flexDirection="column"
          width="100%"
          maxWidth={GALLERY_CONTAINER_WIDTH}
          gap="2x"
          alignItems="flex-end"
          sx={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
          {canManage ? (
            <ImageGridManageControls
              isManaging={isManaging}
              selectedCount={selectedIds.size}
              isSaving={isSaving}
              contrast={contrast}
              gridHeader={gridHeader}
              headerEnd={headerEnd}
              onEnterManageMode={enterManageMode}
              onCancel={cancel}
              onMakePrivate={() => setSelectedPrivacy(false)}
              onMakePublic={() => setSelectedPrivacy(true)}
            />
          ) : (
            <Flex
              flexDirection="row"
              width="100%"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              gap="3x"
            >
              {gridHeader}
              {headerEnd}
            </Flex>
          )}
          <BulkPrivacyFailureMessage failure={failure} contrast={contrast} />
        </Flex>
      )}
      {images && (
        <ImageGridContainer
          images={images}
          dimmedImageIds={dimmedImageIds}
          lockedImageIds={lockedImageIds}
          isManaging={isManaging}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          {...gridProps}
        />
      )}
    </Flex>
  );
};

export default ManagedGalleryGrid;
